// The CAD check — a diff, not a re-entry.
// Answers: does the CAD export agree with the job's items, and where it does not, what was
// decided? The items are the record; the export is a picture of the design. Every difference
// gets a resolution and a one-line note, and the CAD check step cannot close while one is open.

import { useRef, useState } from 'react';
import { parseComparisonCsv, type Difference } from '@zerogap/engine';
import { deleteRow, insertRow, toast, upsertRow } from '../../lib/api';
import { useCadCheck } from '../../lib/cadcheck';
import { longDate } from '../../lib/format';
import { useSession } from '../../lib/session';
import { Btn, Edit, Empty, Info, Ln, Loading, Panel, Tag } from '../../ui/kit';
import { uploadFile } from './Files';
import type { JobCtx } from './Workspace';

const GROUPS: { status: Difference['status']; label: string }[] = [
  { status: 'differs', label: 'Differs' },
  { status: 'only_cad', label: 'Only in the CAD export' },
  { status: 'only_zerogap', label: 'Only in ZeroGap' },
];
const FIELD: Record<string, string> = { width: 'width', height: 'height', depth: 'depth', qty: 'how many', count: 'how many' };

export function CadCheck({ ctx }: { ctx: JobCtx }) {
  const { jobId, r } = ctx;
  const s = useSession();
  const check = useCadCheck(jobId, r.res, r.lib);
  const input = useRef<HTMLInputElement>(null);
  const [type, setType] = useState('');
  const [busy, setBusy] = useState(false);
  const canEdit = s.canEdit('screen.item');
  const mapping = check.sourceTypes.find((m) => m.key === type) ?? check.sourceTypes[0];
  const resolutionFor = (d: Difference) => check.resolutions.find((x) => x.diff_key === d.key);
  const labelOf = (key: string) => check.settings.resolutions?.find((x) => x.key === key)?.label ?? key;

  const importFile = async (list: FileList | null) => {
    const picked = list?.[0];
    if (!picked || !mapping) return;
    setBusy(true);
    try {
      const parsed = parseComparisonCsv(await picked.text(), mapping);
      if (parsed.rows.length === 0) {
        toast(`Nothing could be read from ${picked.name}. ${parsed.skipped[0]?.reason ?? ''}`);
        return;
      }
      const file = await uploadFile(picked, 'job', jobId, s.user?.id ?? null);
      await insertRow('comparison_source', { job_id: jobId, source_type: mapping.key, name: picked.name, file_id: file.id, rows: parsed.rows, skipped: parsed.skipped, imported_by: s.user?.id ?? null });
      toast(`Read ${parsed.rows.length} ${parsed.rows.length === 1 ? 'line' : 'lines'} from ${picked.name}${parsed.skipped.length ? `; ${parsed.skipped.length} could not be read` : ''}.`);
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };

  const resolve = async (d: Difference, resolution: string, note?: string | null) => {
    if (!check.source) return;
    const existing = resolutionFor(d);
    if (!resolution) {
      if (existing) await deleteRow('comparison_resolution', existing.id);
      return;
    }
    await upsertRow(
      'comparison_resolution',
      { comparison_source_id: check.source.id, diff_key: d.key, resolution, note: note === undefined ? existing?.note ?? null : note, resolved_by: s.user?.id ?? null },
      'comparison_source_id,diff_key',
    );
  };

  if (check.loading) return <Loading what="Loading the CAD check" />;
  const skipped = ((check.source?.skipped ?? []) as { line: number; reason: string }[]);
  const matches = check.diffs.filter((d) => d.status === 'match');
  const columns = mapping ? (['kind', 'number', 'parent', 'width', 'height', 'depth', 'material', 'qty'] as const).map((k) => mapping.columns[k]).filter(Boolean).join(', ') : '';

  return (
    <div className="stack">
      <Panel
        label="CAD export"
        right={<Info left>The job's items are what the shop builds to and buys from. A CAD export is brought in only to be compared with them: nothing here changes an item. To fix a difference, change the item (or leave it and say why).</Info>}
      >
        {check.source ? (
          <Ln
            left={
              <>
                <b>{check.source.name ?? 'CAD export'}</b> · {check.sourceTypes.find((m) => m.key === check.source!.source_type)?.label ?? check.source.source_type} · imported {longDate(check.source.created_at)} ·{' '}
                {(check.source.rows as unknown[]).length} lines read
                {check.sources.length > 1 && <small> · {check.sources.length - 1} earlier {check.sources.length === 2 ? 'import' : 'imports'} kept</small>}
              </>
            }
            right={check.blocker ? <Tag kind="un">{check.open.length} to resolve</Tag> : <Tag kind="on">nothing open</Tag>}
          />
        ) : (
          <Empty>No CAD export has been imported for this job yet.</Empty>
        )}
        {skipped.length > 0 && (
          <div className="warn" style={{ marginTop: 6 }}>
            {skipped.length} {skipped.length === 1 ? 'line' : 'lines'} of the file could not be read: {skipped.slice(0, 6).map((x) => `line ${x.line} (${x.reason})`).join('; ')}
            {skipped.length > 6 ? '; …' : ''}
          </div>
        )}
        {canEdit && (
          <div className="row mid" style={{ marginTop: 10, gap: 6 }}>
            <select value={mapping?.key ?? ''} onChange={(e) => setType(e.target.value)}>
              {check.sourceTypes.map((m) => (
                <option key={m.key} value={m.key}>
                  {m.label}
                </option>
              ))}
            </select>
            <input ref={input} type="file" accept=".csv,text/csv,text/plain" className="hide" onChange={(e) => void importFile(e.target.files)} />
            <Btn primary disabled={busy || !mapping} onClick={() => input.current?.click()}>
              {busy ? 'Reading…' : check.source ? 'Import a newer export' : 'Import the CAD export'}
            </Btn>
            {mapping && <small>Columns this type reads: {columns}. Change them under Settings → CAD export types.</small>}
          </div>
        )}
      </Panel>

      {check.source &&
        GROUPS.map((g) => {
          const list = check.diffs.filter((d) => d.status === g.status);
          if (list.length === 0) return null;
          return (
            <Panel key={g.status} label={`${g.label} · ${list.length}`} soft={list.some((d) => !resolutionFor(d))}>
              {list.map((d) => {
                const done = resolutionFor(d);
                return (
                  <Ln
                    key={d.key}
                    left={
                      <>
                        <b>{d.label}</b>
                        {d.field && <> · {FIELD[d.field] ?? d.field}</>}
                        <div>
                          <small>
                            CAD: {d.cad ?? 'not there'} · ZeroGap: {d.zerogap ?? 'not there'}
                          </small>
                        </div>
                      </>
                    }
                    right={
                      <span className="row mid" style={{ gap: 6 }}>
                        <select disabled={!canEdit} value={done?.resolution ?? ''} onChange={(e) => void resolve(d, e.target.value)}>
                          <option value="">Not resolved</option>
                          {(check.settings.resolutions ?? []).map((x) => (
                            <option key={x.key} value={x.key}>
                              {x.label}
                            </option>
                          ))}
                          {done && !(check.settings.resolutions ?? []).some((x) => x.key === done.resolution) && <option value={done.resolution}>{labelOf(done.resolution)}</option>}
                        </select>
                        <Edit disabled={!canEdit || !done} value={done?.note ?? ''} placeholder={done ? 'One line: why' : 'Pick a resolution first'} onSave={(v) => resolve(d, done!.resolution, v || null)} />
                      </span>
                    }
                  />
                );
              })}
            </Panel>
          );
        })}

      {check.source && (
        <Panel label={`Matches · ${matches.length}`}>
          {matches.length === 0 ? (
            <Empty>Nothing matches yet.</Empty>
          ) : (
            <details>
              <summary>
                <small>{matches.length} {matches.length === 1 ? 'line agrees' : 'lines agree'} between the export and the items · show</small>
              </summary>
              {matches.map((d) => (
                <Ln key={d.key} left={d.label} right={d.cad && d.field ? `${d.cad}` : 'same'} />
              ))}
            </details>
          )}
        </Panel>
      )}
    </div>
  );
}
